import { JSX } from 'preact';
import { ReactNode } from 'preact/compat';
import { useEffect } from 'preact/hooks';

import { useCSSViewportHeightFix, useWindowSize } from '@/hooks';

import { IsMobileLayoutContext } from './contexts';
import MobileLayout from './MobileLayout';

const MOBILE_LAYOUT_WIDTH = 768; // tablet portrait width
// lg-1 (1024px - 1): routes use this so tablet landscape stays in mobile layout, preventing orientation-triggered layout shifts
export const MOBILE_LAYOUT_BREAKPOINT = 1023;

interface BaseProps {
  breakPoint?: number;
}

/** A page built as a desktop/mobile pair; the mobile half is wrapped in the app's mobile chrome. */
interface SplitLayoutProps extends BaseProps {
  desktop: JSX.Element;
  mobile: JSX.Element;
  title: string;
  render?: never;
}

/**
 * A page that is one tree on both layouts and brings its own chrome. It still gets the body class
 * and the context, which is how components elsewhere — the Speedy Flow's modal, for one — know
 * which layout they are in.
 */
interface RenderLayoutProps extends BaseProps {
  render: (isMobileLayout: boolean) => ReactNode;
  desktop?: never;
  mobile?: never;
  title?: never;
}

type LayoutDetectorProps = SplitLayoutProps | RenderLayoutProps;

export const LayoutDetector = ({
  desktop,
  mobile,
  // Only the split form has a title, and the pages using it pass an empty one anyway.
  title = '',
  render,
  breakPoint = MOBILE_LAYOUT_WIDTH,
}: LayoutDetectorProps): JSX.Element => {
  useCSSViewportHeightFix();
  const [windowWidth] = useWindowSize();
  const isMobileLayout = windowWidth <= breakPoint;

  useEffect(() => {
    document.body.classList.add(isMobileLayout ? 'group/mobile-layout' : 'group/desktop-layout');
    return () => {
      document.body.classList.remove('group/mobile-layout', 'group/desktop-layout');
    };
  }, [isMobileLayout]);

  const renderContent = () => {
    if (render) {
      return render(isMobileLayout);
    }
    return isMobileLayout ? <MobileLayout title={title}>{mobile}</MobileLayout> : desktop;
  };

  return <IsMobileLayoutContext.Provider value={isMobileLayout}>{renderContent()}</IsMobileLayoutContext.Provider>;
};
